
import { FC, useEffect } from "react";
import { useFormContext, useWatch } from "react-hook-form";

import { Textbox } from "capitalrx-components-2.0/lib/forms";
import { ProviderManagement } from "@/api/interfaces";

const tinLengthSsn = 11; // ###-##-####
const tinLengthEin = 10; // ##-#######
const TIN_DIGIT_LENGTH = 9;

const onlyDigits = (s: string) => s.replace(/\D/g, "");
export const tinTrim = (val: string) => val.replace(/-/g, "");

export const formatValueTin = (val: string, designation?: string) => {
  if (!val) return "";
  const digits = onlyDigits(val);

  if (designation === ProviderManagement.DESIGNATION_TYPE_VALUES.SSN) {
    return digits
      .replace(/(\d{3})(\d{2})(\d{4})/, "$1-$2-$3")
      .slice(0, tinLengthSsn);
  }

  if (designation === ProviderManagement.DESIGNATION_TYPE_VALUES.EIN) {
    return digits
      .replace(/(\d{2})(\d{7})/, "$1-$2")
      .slice(0, tinLengthEin);
  }

  // If designation is ALL or undefined → default to SSN-like progressive mask
  const part1 = digits.slice(0, 3);
  const part2 = digits.slice(3, 5);
  const part3 = digits.slice(5, 9);
  return [part1, part2, part3].filter(Boolean).join("-").slice(0, tinLengthSsn);
};

export const validateTinFormat = (value?: string, designation?: string): string | undefined => {
  if (!value) return undefined;

  const digits = onlyDigits(value);

  // If designation is ALL or undefined accept SSN/EIN/ITIN patterns or 9 digits
  if (!designation || designation === "ALL") {
    if (digits.length === TIN_DIGIT_LENGTH) return undefined;
    return "Value must be 9 digits.";
  }

  // SSN check
  if (
    designation === ProviderManagement.DESIGNATION_TYPE_VALUES.SSN &&
    value.length === tinLengthSsn
  ) {
    return undefined;
  }

  // EIN check
  if (
    designation === ProviderManagement.DESIGNATION_TYPE_VALUES.EIN &&
    value.length === tinLengthEin
  ) {
    return undefined;
  }

  return "Value must be 9 digits.";
};

export interface TinFieldProps {
  readonly formName: string;
  readonly designationName: string;
  readonly readOnly?: boolean;
}

export const TinField: FC<TinFieldProps> = ({ readOnly, formName, designationName }) => {
  const { setValue, watch, control } = useFormContext();
  const designation = useWatch({ control, name: designationName });
  useEffect(() => {
    const { unsubscribe } = watch((value, { name }) => {
      const maskVal = formatValueTin(value[formName], value[designationName]);
      if ([formName, designationName].includes(name) && value[formName] !== maskVal) {
        setValue(formName, maskVal);
      }
    });
    return () => unsubscribe();
  }, [setValue, watch]);

  return (
    <Textbox.Field
      label="TIN (Tax Identification Number)"
      name={formName}
      testId={formName}
      required
      readOnly={readOnly}
      validator={(value) => validateTinFormat(value, designation)}
    />
  );
};
